import React, { useState, useContext, useEffect, useRef } from 'react'; // Fixed: Added useRef
import { AuthContext } from '../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { requestPasswordReset } from '../utils/auth';
import './AuthForm.css';

const API_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';
// IMPORTANT: Replace with your actual reCAPTCHA Site Key
const RECAPTCHA_SITE_KEY = '6Le1HjcrAAAAAM1ssJZRB9ZSgfhx9UgwkZ3BqERO';

export default function AuthForm({ reportSlug, reportId, reportFilename }) {
  const { jwt, user, signup, signin, signout } = useContext(AuthContext);
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    organization: '',
    email: '',
    password: '',
    newsletterConsent: true,
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const recaptchaRef = useRef(null); // Ref to access the reCAPTCHA instance for reset
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaRenderKey, setRecaptchaRenderKey] = useState(0);


  useEffect(() => {
    if (jwt) {
      console.log('AuthForm - Logged In View Props:', { reportSlug, reportId, reportFilename });
    }
  }, [jwt, reportSlug, reportId, reportFilename]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

     const handleRecaptchaChange = (token) => {
    console.log("reCAPTCHA token obtained via onChange:", token);
    setRecaptchaToken(token);
    setError(''); // Clear previous errors if user completes CAPTCHA
  };

  const handleRecaptchaExpire = () => {
    console.log("reCAPTCHA token expired");
    setRecaptchaToken(null);
    // Optionally, inform the user they need to re-verify
    // setError('reCAPTCHA verification has expired. Please check the box again.');
  };

   const handleRecaptchaError = (error) => {
    console.error("reCAPTCHA widget direct error:", error);
    // Display a generic error to the user, as specific reCAPTCHA errors might not be user-friendly
    setError("An issue occurred with reCAPTCHA. Please refresh and try again.");
    // Ensure token is cleared and widget state might need a reset if possible,
    // though often such errors mean the widget is in a bad state.
    setRecaptchaToken(null);
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    setDownloadError('');

    // For checkbox reCAPTCHA, check the token from state
    if (mode === 'register') {
      if (!recaptchaToken) { // Check the token from state
        setError('Please complete the reCAPTCHA verification by checking the box.');
        setLoading(false);
        return; // Stop submission
      }
      // recaptchaToken from state will be used directly in the signup payload
    }

    try {
      let resp;
      if (mode === 'register') {
        resp = await signup({
          firstName: form.firstName,
          lastName: form.lastName,
          organization: form.organization,
          email: form.email,
          username: form.email, // Assuming username is email
          password: form.password,
          newsletterConsent: form.newsletterConsent,
          recaptchaToken: recaptchaToken, // Use the token from state
        });
      } else if (mode === 'forgot') {
        await requestPasswordReset(form.email);
        setNotice('If that email is registered, a reset link has been sent. Please check your spam or junk folder if it does not arrive.');
        setForm((prev) => ({ ...prev, password: '' }));
        setLoading(false);
        return;
      } else {
        resp = await signin({ identifier: form.email, password: form.password });
      }
      setLoading(false);
      if (resp.error) throw new Error(resp.error.message || 'Authentication failed');
      
      // Reset reCAPTCHA and its token state on successful submission for register mode
      if (mode === 'register' && recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
      // Reset reCAPTCHA and its token state on error for register mode, so user can try again
      if (mode === 'register' && recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    }
  };

  const handleDownload = async () => {
    console.log('AuthForm - handleDownload called with current props:', { reportSlug, reportId, reportFilename });
    if (!jwt) {
      setDownloadError('Authentication required to download.');
      return;
    }
    if (!reportSlug) {
      setDownloadError('Report slug is missing, cannot download.');
      return;
    }
    setDownloadLoading(true);
    setDownloadError('');
    try {
      const response = await fetch(`${API_URL}/api/reports/${reportSlug}/download`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${jwt}` },
      });
      if (!response.ok) {
        let errorDetails = `Download failed: ${response.statusText}`;
        try {
          const errData = await response.json();
          errorDetails = errData.error?.details?.message || errData.error?.message || errorDetails;
        } catch (jsonErr) { /* Stick with statusText */ }
        throw new Error(`Status ${response.status}: ${errorDetails}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', reportFilename || `${reportSlug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('AuthForm - Download error:', err);
      setDownloadError(err.message);
    } finally {
      setDownloadLoading(false);
    }
  };

 const switchMode = (newMode) => {
  setMode(newMode);
  setError('');
  setNotice('');
  setForm(prev => ({ ...prev, password: '' }));
  setRecaptchaToken(null);
  if (newMode === 'register') {
    setRecaptchaRenderKey(prevKey => prevKey + 1); // Change key to force remount
  }
};


  return (
    <div className="authform-container-full-width">
      <div className="authform-content-area">
        <h2>Download the report</h2>
        {jwt ? (
          <div className="authform-loggedin-view">
            <p>Welcome back, {user?.firstName || user?.email || 'User'}!</p>
            {reportSlug && reportId ? (
              <div>
                <button onClick={handleDownload} disabled={downloadLoading} className="button">
                  {downloadLoading ? 'Downloading...' : 'Download Report'}
                </button>
                {downloadError && <p style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.9em' }}>{downloadError}</p>}
              </div>
            ) : (
              <p style={{ color: 'orange', fontSize: '0.8em' }}>Download details not available for this report.</p>
            )}
            <button onClick={signout} className="button button-logout-authform">Logout</button>
          </div>
        ) : (
          <div className="authform-loggedout-view">
            <div className="auth-tabs">
              <button
                className={`auth-tab ${mode === 'register' ? 'active' : ''}`}
                onClick={() => switchMode('register')}
                disabled={loading}
              >
                Sign-up
              </button>
              <button
                className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
                onClick={() => switchMode('login')}
                disabled={loading}
              >
                Login
              </button>
            </div>
            <form id={mode === 'register' ? "signup-form" : mode === 'forgot' ? "forgot-password-form" : "login-form"} onSubmit={handleAuthSubmit}>
              {mode === 'register' && (
                <>
                  <div className="form-row-triplet">
                    <div className="mc-field-group form-group-inline">
                      <label htmlFor="firstName">First Name *</label>
                      <input type="text" name="firstName" id="firstName" value={form.firstName} onChange={handleChange} required disabled={loading} />
                    </div>
                    <div className="mc-field-group form-group-inline">
                      <label htmlFor="lastName">Last Name *</label>
                      <input type="text" name="lastName" id="lastName" value={form.lastName} onChange={handleChange} required disabled={loading} />
                    </div>
                    <div className="mc-field-group form-group-inline">
                      <label htmlFor="organization">Organization *</label>
                      <input type="text" name="organization" id="organization" value={form.organization} onChange={handleChange} required disabled={loading} />
                    </div>
                  </div>
                  <div className="form-row-pair">
                    <div className="mc-field-group form-group-inline">
                      <label htmlFor="email">Email Address *</label>
                      <input type="email" name="email" id="email" value={form.email} onChange={handleChange} required disabled={loading} />
                    </div>
                    <div className="mc-field-group form-group-inline">
                      <label htmlFor="password">Password *</label>
                      <input type="password" name="password" id="password" value={form.password} onChange={handleChange} required disabled={loading} />
                    </div>
                  </div>
                  <div className="mergeRow gdpr-mergeRow mc-field-group">
                    <fieldset className="mc_fieldset gdprRequired mc-field-group">
                      <label className="checkbox subfield" htmlFor="newsletterConsent">
                        <input type="checkbox" name="newsletterConsent" id="newsletterConsent" checked={form.newsletterConsent} onChange={handleChange} disabled={loading} />
                        I agree to sign up to the Nexed Insight newsletters *
                      </label>
                    </fieldset>
                  </div>
                  
                  {/* reCAPTCHA v2 Checkbox - Visible */}
                  <div className="mc-field-group" style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
                     <ReCAPTCHA
                      key={recaptchaRenderKey}
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={handleRecaptchaExpire}
                      onError={handleRecaptchaError}
                    />
                  </div>
                  <div className="clear">
                    <button type="submit" className="button" disabled={loading}>
                      {loading ? 'Processing...' : 'Register & Subscribe'}
                    </button>
                  </div>
                </>
              )}
              {mode === 'login' && (
                <>
                  <div className="mc-field-group">
                    <label htmlFor="login-email">Email Address *</label>
                    <input type="email" name="email" id="login-email" value={form.email} onChange={handleChange} required disabled={loading} />
                  </div>
                  <div className="mc-field-group">
                    <label htmlFor="login-password">Password *</label>
                    <input type="password" name="password" id="login-password" value={form.password} onChange={handleChange} required disabled={loading} />
                  </div>
                  <button
                    type="button"
                    className="auth-link-button"
                    onClick={() => switchMode('forgot')}
                    disabled={loading}
                  >
                    Forgot password?
                  </button>
                  <div className="clear">
                    <button type="submit" className="button" disabled={loading}>
                      {loading ? 'Processing...' : 'Login'}
                    </button>
                  </div>
                </>
              )}
              {mode === 'forgot' && (
                <>
                  <p className="authform-helper-text">
                    Enter your email address and we will send a reset link if the account exists. If you do not see it after a few minutes, please check your spam or junk folder.
                  </p>
                  <div className="mc-field-group">
                    <label htmlFor="forgot-email">Email Address *</label>
                    <input type="email" name="email" id="forgot-email" value={form.email} onChange={handleChange} required disabled={loading} />
                  </div>
                  <div className="clear">
                    <button type="submit" className="button" disabled={loading}>
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="auth-link-button auth-link-button-centered"
                    onClick={() => switchMode('login')}
                    disabled={loading}
                  >
                    Back to login
                  </button>
                </>
              )}
              {error && <div className="response" id="mce-error-response" style={{ marginTop: '1rem' }}>{error}</div>}
              {notice && <div className="response authform-success-response" style={{ marginTop: '1rem' }}>{notice}</div>}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
